/**
 * BlogCallout — Visual callout boxes for blog articles.
 *
 * Supports 5 types, each with distinct colors, icons, and styling:
 * - tip: Teal — parent/teacher tips
 * - experiment: Amber — hands-on STEAM experiments
 * - age: Purple — age-band specific guidance
 * - fact: Green — fun science facts
 * - question: Brand teal — open-ended wonder questions
 *
 * Triggered by markdown blockquote syntax:
 *   > [!tip] Your tip here
 *   > [!experiment] Experiment details
 *   > [!age:4-7] Content for youngest
 *   > [!fact] Fun science fact
 *   > [!question] Open-ended wonder question
 */

import { useState, type ReactNode } from 'react';
import { useLangStore } from '../../store/langStore';

export type CalloutType = 'tip' | 'experiment' | 'age' | 'fact' | 'question' | 'answer';

interface BlogCalloutProps {
  type: CalloutType;
  ageRange?: string;        // e.g. "4-7", "8-12", "13-16"
  children: ReactNode;
}

const CALLOUT_STYLES: Record<CalloutType, {
  bg: string;
  border: string;
  iconBg: string;
  icon: string;
  titleNo: string;
  titleEn: string;
}> = {
  tip: {
    bg: 'bg-[#F0FDFA]',
    border: 'border-[#00C2A8]',
    iconBg: 'bg-[#CCFBF1]',
    icon: '💡',
    titleNo: 'Tips',
    titleEn: 'Tip',
  },
  experiment: {
    bg: 'bg-[#FFFBEB]',
    border: 'border-[#F59E0B]',
    iconBg: 'bg-[#FEF3C7]',
    icon: '🧪',
    titleNo: 'Eksperiment',
    titleEn: 'Experiment',
  },
  age: {
    bg: 'bg-[#FAF5FF]',
    border: 'border-[#9333EA]',
    iconBg: 'bg-[#F3E8FF]',
    icon: '👶',
    titleNo: 'Aldersgruppe',
    titleEn: 'Age Group',
  },
  fact: {
    bg: 'bg-[#F0FDF4]',
    border: 'border-[#16A34A]',
    iconBg: 'bg-[#DCFCE7]',
    icon: '🔬',
    titleNo: 'Visste du?',
    titleEn: 'Did you know?',
  },
  question: {
    bg: 'bg-gradient-to-r from-[#F0FDFA] to-[#FAF5EA]',
    border: 'border-[#00C2A8]',
    iconBg: 'bg-[#CCFBF1]',
    icon: '🤔',
    titleNo: 'Tenk på dette',
    titleEn: 'Think about this',
  },
  answer: {
    bg: 'bg-[#FFF7ED]',
    border: 'border-[#F97316]',
    iconBg: 'bg-[#FFEDD5]',
    icon: '🔓',
    titleNo: 'Vis svaret',
    titleEn: 'Reveal answer',
  },
};

const AGE_ICONS: Record<string, string> = {
  '4-7': '🌱',
  '8-12': '🌿',
  '10-12': '🌿',
  '13-16': '🌳',
};

export function BlogCallout({ type, ageRange, children }: BlogCalloutProps) {
  const style = CALLOUT_STYLES[type];
  const ageIcon = ageRange ? (AGE_ICONS[ageRange] || '👤') : null;
  const { lang } = useLangStore();
  const title = lang === 'en' ? style.titleEn : style.titleNo;

  // Answer type renders as a click-to-reveal box
  if (type === 'answer') {
    return <AnswerReveal style={style} lang={lang}>{children}</AnswerReveal>;
  }

  return (
    <div
      data-callout={type}
      className={`
        ${style.bg} border-l-4 ${style.border}
        rounded-r-xl px-5 py-4 my-8
        shadow-sm
      `}
    >
      {/* Header row */}
      <div className="flex items-center gap-2.5 mb-2">
        <span
          className={`${style.iconBg} w-8 h-8 rounded-lg flex items-center justify-center text-base`}
        >
          {type === 'age' && ageIcon ? ageIcon : style.icon}
        </span>
        <span className="font-display font-semibold text-ink text-sm uppercase tracking-wide">
          {type === 'age' && ageRange
            ? `${ageRange} ${lang === 'en' ? 'years' : 'år'}`
            : title}
        </span>
      </div>

      {/* Content */}
      <div className="font-body text-ink text-base leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0">
        {children}
      </div>
    </div>
  );
}

function AnswerReveal({ style, lang, children }: { style: typeof CALLOUT_STYLES['answer']; lang: 'no' | 'en'; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const en = lang === 'en';

  return (
    <div
      data-callout="answer"
      className={`
        ${style.bg} border-l-4 ${style.border}
        rounded-r-xl my-8 shadow-sm overflow-hidden
      `}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5 px-5 py-4 cursor-pointer select-none text-left"
      >
        <span
          className={`${style.iconBg} w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0`}
        >
          {open ? '🔓' : '🔒'}
        </span>
        <span className="font-display font-semibold text-ink text-sm uppercase tracking-wide">
          {open ? (en ? 'Answer' : 'Svaret') : (en ? 'Reveal answer' : 'Vis svaret')}
        </span>
        <span className="ml-auto text-ink-faint text-xs font-display">
          {open ? (en ? '▲ Hide' : '▲ Skjul') : (en ? '▶ Tap to reveal' : '▶ Trykk for å se')}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4 font-body text-ink text-base leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0 border-t border-orange-100">
          {children}
        </div>
      )}
    </div>
  );
}
